/** Anything laid out as a parent-pointer tree: org units, hierarchy nodes, … */
export interface ParentLinked {
  id: string;
  parentId?: string | null;
}

/**
 * A node plus every descendant of it.
 *
 * Used to decide which nodes a move dialog must not offer as the new parent: re-parenting a node
 * onto its own descendant would cut the whole subtree loose from every root. The server rejects it
 * too — this only keeps the impossible choice off the screen.
 *
 * Breadth-first over the adjacency list, `has` before `add`, so a tree that already contains a
 * cycle terminates instead of spinning.
 */
export function collectSubtreeIds<T extends ParentLinked>(
  nodes: readonly T[],
  rootId: string,
): Set<string> {
  const childrenByParent = new Map<string, string[]>();
  for (const node of nodes) {
    if (!node.parentId) continue;
    const siblings = childrenByParent.get(node.parentId);
    if (siblings) siblings.push(node.id);
    else childrenByParent.set(node.parentId, [node.id]);
  }

  const subtree = new Set<string>([rootId]);
  const queue = [rootId];
  while (queue.length > 0) {
    const current = queue.pop()!;
    for (const childId of childrenByParent.get(current) ?? []) {
      if (subtree.has(childId)) continue;
      subtree.add(childId);
      queue.push(childId);
    }
  }
  return subtree;
}
